/**
 * The settings page's form model: turning `InvoicingSettings` into an editable
 * draft and back into a `PUT /v1/invoicing/settings` body (ALL-5908,
 * page spec `invoicing-settings`).
 *
 * Kept free of React so the two things most likely to be wrong — "did the draft
 * carry a value the operator did not type" and "is the wire amount still base
 * units" — can be tested without mounting a page.
 *
 * Two rules the contract forces on this module:
 *
 *   - **The legal name is never part of the draft.** `kybLegalName` is the org's
 *     KYB record, read-only by contract; it is not in `SettingsDraft` and never
 *     reaches the update body, so no edit path can exist for it.
 *   - **Money stays a base-unit string.** A limit is edited as the decimal the
 *     operator typed, converted with `parseDecimalToBaseUnits` (BigInt, never a
 *     JS number) for the wire, and formatted back with `formatMoney`. A value
 *     with more fractional digits than the asset has is refused, not rounded.
 */

import {
  MoneyError,
  formatMoney,
  parseDecimalToBaseUnits,
} from "@/features/invoicing/money";
import type {
  CollectionWallet,
  InvoicingSettings,
  InvoicingSettingsUpdate,
  Money,
  NetworkLimit,
} from "@/features/invoicing/types";

/** A collection wallet row as the form holds it. */
export interface WalletDraft {
  network: string;
  assetSymbol: string;
  walletId: string;
  validatedAt?: string;
}

/** A network-limit row as the form holds it: amounts are what a human typed. */
export interface LimitDraft {
  network: string;
  assetSymbol: string;
  decimals: number;
  dustThreshold: string;
  minInvoiceAmount: string;
}

export interface SettingsDraft {
  defaultTermsDays: string;
  expiryGraceDays: string;
  collectionWallets: WalletDraft[];
  networkLimits: LimitDraft[];
}

/**
 * The asset a network limit's amounts are denominated in. Read from the
 * `dustThreshold` Money the service sent — the contract's limit has no separate
 * `asset` field — and never assumed when the service left it out.
 */
function decimalsOf(limit: NetworkLimit): { symbol: string; decimals: number } {
  return {
    symbol: limit.dustThreshold?.asset?.symbol ?? limit.minInvoiceAmount?.asset?.symbol ?? "",
    decimals: limit.dustThreshold?.asset?.decimals ?? limit.minInvoiceAmount?.asset?.decimals ?? 0,
  };
}

/** A limit row's edit value: the human decimal form, or the raw base units if the asset is unreadable. */
function limitEditValue(limit: Money): string {
  try {
    return formatMoney(limit);
  } catch {
    // A Money the money helpers refuse is not a value we can format; showing the
    // base-unit string the service sent is honest and keeps the row readable.
    return typeof limit.amount === "string" ? limit.amount : "";
  }
}

export function draftFromSettings(settings: InvoicingSettings): SettingsDraft {
  return {
    defaultTermsDays: String(settings.defaultTermsDays),
    expiryGraceDays: String(settings.expiryGraceDays),
    collectionWallets: settings.collectionWallets.map((wallet) => ({
      network: wallet.network,
      assetSymbol: wallet.assetSymbol,
      walletId: wallet.walletId,
      validatedAt: wallet.validatedAt,
    })),
    networkLimits: settings.networkLimits.map((limit) => {
      const asset = decimalsOf(limit);
      return {
        network: limit.network,
        assetSymbol: asset.symbol,
        decimals: asset.decimals,
        dustThreshold: limitEditValue(limit.dustThreshold),
        minInvoiceAmount: limitEditValue(limit.minInvoiceAmount),
      };
    }),
  };
}

/** True when nothing in the draft differs from what the service sent. */
export function draftEquals(left: SettingsDraft, right: SettingsDraft): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** A whole, non-negative number, or null. Never clamps: a range the service owns stays the service's. */
export function parseWholeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

/** True when the wallet has been certified for its network (the contract's `validatedAt`). */
export function isCertified(wallet: CollectionWallet | WalletDraft): boolean {
  return typeof wallet.validatedAt === "string" && wallet.validatedAt.length > 0;
}

export type BuildUpdateResult =
  | { ok: true; body: InvoicingSettingsUpdate }
  | { ok: false; errors: string[] };

/**
 * The PUT body for this draft, or the list of fields that cannot be sent.
 *
 * `version` is carried from the settings the draft was built from: it is the
 * optimistic-concurrency token, and a mismatch is the service's 409
 * `VERSION_CONFLICT`, which the page shows rather than retrying.
 */
export function buildUpdate(settings: InvoicingSettings, draft: SettingsDraft): BuildUpdateResult {
  const errors: string[] = [];

  const defaultTermsDays = parseWholeNumber(draft.defaultTermsDays);
  if (defaultTermsDays === null) {
    errors.push("Default terms (days): enter a whole number.");
  }
  // Deliberately NOT bounded here: the service owns the 0..90 window and refuses
  // a value outside it. Clamping in the client would save a number the operator
  // never chose and hide the refusal the contract exists to produce.
  const expiryGraceDays = parseWholeNumber(draft.expiryGraceDays);
  if (expiryGraceDays === null) {
    errors.push("Expiry grace (days): enter a whole number.");
  }

  const collectionWallets: CollectionWallet[] = [];
  for (const wallet of draft.collectionWallets) {
    const walletId = wallet.walletId.trim();
    if (walletId === "") {
      errors.push(`Collection wallet for ${wallet.network} ${wallet.assetSymbol}: enter a wallet.`);
      continue;
    }
    // The wallet the service actually sent for this network and asset, if any.
    const certified = settings.collectionWallets.find(
      (row) => row.network === wallet.network && row.assetSymbol === wallet.assetSymbol,
    );
    // `validatedAt` is the service's verdict on ONE wallet id, so it is carried
    // only while that exact wallet id is still the one named here. Carrying it
    // onto an id the operator just typed would tell the service a wallet it has
    // never seen is certified.
    const stillTheCertifiedWallet =
      certified !== undefined
      && isCertified(certified)
      && certified.validatedAt !== undefined
      && certified.walletId === walletId;
    collectionWallets.push({
      network: wallet.network,
      assetSymbol: wallet.assetSymbol,
      walletId,
      ...(stillTheCertifiedWallet ? { validatedAt: certified.validatedAt } : {}),
    });
  }

  const networkLimits: InvoicingSettings["networkLimits"] = [];
  for (const limit of draft.networkLimits) {
    const dust = convertAmount(limit.dustThreshold, limit.decimals, limit.network, "dust threshold", errors);
    const min = convertAmount(
      limit.minInvoiceAmount,
      limit.decimals,
      limit.network,
      "minimum invoice amount",
      errors,
    );
    if (!dust || !min) continue;
    networkLimits.push({
      network: limit.network,
      dustThreshold: { amount: dust, asset: { symbol: limit.assetSymbol, decimals: limit.decimals } },
      minInvoiceAmount: { amount: min, asset: { symbol: limit.assetSymbol, decimals: limit.decimals } },
    });
  }

  if (errors.length > 0 || defaultTermsDays === null || expiryGraceDays === null) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    body: {
      version: settings.version,
      defaultTermsDays,
      expiryGraceDays,
      collectionWallets,
      networkLimits,
    },
  };
}

/** One amount, converted to base units, or null after recording why it could not be. */
function convertAmount(
  value: string,
  decimals: number,
  network: string,
  label: string,
  errors: string[],
): string | null {
  if (value.trim() === "") {
    errors.push(`${network} ${label}: enter an amount.`);
    return null;
  }
  try {
    return parseDecimalToBaseUnits(value, decimals);
  } catch (error) {
    const detail = error instanceof MoneyError ? error.message : "not a usable amount.";
    errors.push(`${network} ${label}: ${detail}`);
    return null;
  }
}
